import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Paper, List, ListItem, ListItemButton, Button, Typography, IconButton, Divider, Tabs, Tab, FormControlLabel, Switch, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArcGISMap from '@arcgis/core/Map';
import MapView from '@arcgis/core/views/MapView';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import WebTileLayer from "@arcgis/core/layers/WebTileLayer";
import DistanceMeasurement2D from "@arcgis/core/widgets/DistanceMeasurement2D";
import ScaleBar from "@arcgis/core/widgets/ScaleBar";
import Point from "@arcgis/core/geometry/Point";
import TextSymbol from "@arcgis/core/symbols/TextSymbol";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import axios from 'axios';
import moment from 'moment';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MenuIcon from '@mui/icons-material/Menu';
// Utilidades propias
import { buildDirectionArrowsGraphic } from '../utils/routeArrows';

// Importar componentes personalizados
import SearchVehicle from './SearchVehicle';
import PorticoSearch from './PorticoSearch';
import DateTimeSelector from './DateTimeSelector';
import PorticoLayer from './PorticoLayer';
import PorticoPopup from './PorticoPopup';
import PointPopup from './PointPopup';
import RoutePlayback from './RoutePlayback';

// Construye los Graphics de la capa "SmartMatch Real" (línea ajustada ámbar,
// marcadores de límite de segmento, marcadores de pórtico detectado en
// magenta) a partir de la respuesta de /route/smartmatch-real o
// /ruta/:id/smartmatch-real. Función pura (sin estado de React) para poder
// llamarla tanto desde el flujo de búsqueda por vehículo/fecha (vía
// displaySmartMatchRealRoute) como desde el efecto de montaje que carga un
// GPX subido a mano (que no puede depender de estado de React todavía no
// actualizado en ese punto del ciclo de vida).
function buildSmartMatchRealGraphics(data) {
  const graphics = [];

  // Línea ajustada: concatenar las coordenadas de todos los segmentos matcheados
  (data.smartmatch_coordinates || []).forEach(seg => {
    if (!seg.coordinates || seg.coordinates.length < 2) return;
    const pathCoordinates = seg.coordinates.map(c => [c.longitude, c.latitude]);
    graphics.push(new Graphic({
      geometry: { type: "polyline", paths: [pathCoordinates] },
      symbol: {
        type: "simple-line",
        color: [255, 193, 7, 0.95],
        width: 4,
        style: "dash"
      },
      attributes: { smartMatchRealType: 'ajustada', segmento: seg.indice }
    }));
  });

  // Marcadores por límite de segmento
  (data.segments || []).forEach(seg => {
    const color = seg.status === 'matched'
      ? [255, 193, 7, 0.95]
      : seg.saltoGPS === 1
        ? [255, 140, 0, 0.9]
        : [128, 128, 128, 0.8];

    [seg.startPoint, seg.endPoint].forEach((p, idx) => {
      if (!p) return;
      graphics.push(new Graphic({
        geometry: new Point({ x: p.longitude, y: p.latitude, spatialReference: { wkid: 4326 } }),
        symbol: new SimpleMarkerSymbol({
          color,
          size: 7,
          outline: { color: [255, 255, 255, 0.9], width: 1 }
        }),
        attributes: {
          smartMatchRealType: 'segmentBoundary',
          segmento: seg.indice,
          bordeIndice: idx,
          status: seg.status,
          saltoGPS: seg.saltoGPS,
          motivo: seg.motivo,
          hora: p.time
        }
      }));
    });
  });

  // Marcadores de pórtico detectado (magenta): anillo grande y hueco (sin
  // relleno) para que quede visible ALREDEDOR del punto amarillo/de color de
  // la capa "Mostrar Pórticos" en vez de tapado debajo — así, al prender y
  // apagar esa capa, se ve si el anillo realmente encierra un pórtico real.
  (data.detected_porticos || []).forEach((portico, idx) => {
    if (portico.x == null || portico.y == null) return;
    graphics.push(new Graphic({
      geometry: new Point({ x: portico.x, y: portico.y, spatialReference: { wkid: 4326 } }),
      symbol: new SimpleMarkerSymbol({
        style: 'circle',
        color: [233, 30, 99, 0.15],
        size: 30,
        outline: { color: [233, 30, 99, 1], width: 3.5 }
      }),
      attributes: {
        smartMatchRealType: 'porticoDetectado',
        index: idx,
        segmento: portico.segmento,
        tipo: portico.tipo,
        id_portico: portico.id_portico || portico.id_carretera_portico,
        nombre: portico.nombre,
        fechaloc: portico.fechaloc
      }
    }));
  });

  return graphics;
}

const MapViewComponent = () => {
  const { id } = useParams();
  const mapDiv = useRef(null);
  const measurementContainerRef = useRef(null);
  const [routeDetails, setRouteDetails] = useState(null);
  const [view, setView] = useState(null);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [pointMarkersLayer, setPointMarkersLayer] = useState(null);

  // Estado para el drawer lateral
  //const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);

  // Estado para vehículos y rutas
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [vehicleRoute, setVehicleRoute] = useState(null);
  const [vehicleRouteLayer, setVehicleRouteLayer] = useState(null);

  // Estado para pórticos
  const [showPorticoLayer, setShowPorticoLayer] = useState(false);
  const [selectedPortico, setSelectedPortico] = useState(null);
  const [porticoPopupVisible, setPorticoPopupVisible] = useState(false);
  const [porticoPopupPosition, setPorticoPopupPosition] = useState({ x: 0, y: 0 });
  // Nuevos estados para controlar la visibilidad de las rutas
  const [showOriginalRoute, setShowOriginalRoute] = useState(true);
  const [showMapmatchedRoute, setShowMapmatchedRoute] = useState(true);

  // Capa "SmartMatch Real": opt-in porque pega al pipeline real de producción
  // (smartmatch-api vía proxy + tag_fnc_detecta_porticos), no una aproximación.
  const [showSmartMatchRealRoute, setShowSmartMatchRealRoute] = useState(false);
  const [smartMatchRealLayer, setSmartMatchRealLayer] = useState(null);
  const [smartMatchRealData, setSmartMatchRealData] = useState(null);
  const [smartMatchRealLoading, setSmartMatchRealLoading] = useState(false);
  const [smartMatchRealError, setSmartMatchRealError] = useState(null);

  const [measurementWidget, setMeasurementWidget] = useState(null);
  const [isMeasuring, setIsMeasuring] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [pointPopupVisible, setPointPopupVisible] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [pointPopupPosition, setPointPopupPosition] = useState({ x: 0, y: 0 });

  // Estado para la reproducción de ruta
  const [playbackEnabled, setPlaybackEnabled] = useState(false);
  const [currentPlaybackPosition, setCurrentPlaybackPosition] = useState(0);

  useEffect(() => {
    if (mapDiv.current) {
      // Crear capa de OpenStreetMap
      const osmLayer = new WebTileLayer({
        urlTemplate: "https://{subDomain}.tile.openstreetmap.org/{level}/{col}/{row}.png",
        subDomains: ["a", "b", "c"],
        copyright: "© OpenStreetMap contributors"
      });

      // Inicializar el mapa
      const map = new ArcGISMap({
        layers: [osmLayer]
      });

      // Crear la vista
      const mapView = new MapView({
        container: mapDiv.current,
        map: map,
        zoom: 15,
        center: [-70.6483, -33.4569], // Santiago, Chile
        constraints: {
          snapToZoom: false
        }
      });

      setView(mapView);

      // Añadir widget de medición en contenedor propio (sobre la barra de
      // playback, que taparía la posición bottom-left de view.ui)
      const measurement = new DistanceMeasurement2D({
        view: mapView,
        unit: "kilometers",
        container: measurementContainerRef.current
      });

      setMeasurementWidget(measurement);

      // Añadir panel de filtros debajo del widget de medición
      const filterContainer = document.createElement('div');
      filterContainer.className = 'esri-widget filter-container';
      filterContainer.style.padding = '10px';
      filterContainer.style.backgroundColor = 'white';
      filterContainer.style.margin = '10px';
      filterContainer.style.borderRadius = '4px';
      filterContainer.style.boxShadow = '0 1px 4px rgba(0,0,0,0.3)';
      mapView.ui.add(filterContainer, "top-right");

      // Añadir widget de escala
      const scaleBar = new ScaleBar({
        view: mapView,
        unit: "metric",
        style: "line"
      });
      mapView.ui.add(scaleBar, "bottom-right");

      // Crear capas para las rutas
      const originalLayer = new GraphicsLayer();
      const matchedLayer = new GraphicsLayer();
      const arrowsLayer = new GraphicsLayer();
      const markersLayer = new GraphicsLayer();
      const vehicleLayer = new GraphicsLayer();
      const smartMatchRealGraphicsLayer = new GraphicsLayer();

      map.add(originalLayer);
      map.add(matchedLayer);
      map.add(arrowsLayer);
      map.add(markersLayer);
      map.add(vehicleLayer);
      map.add(smartMatchRealGraphicsLayer);

      setPointMarkersLayer(markersLayer);
      setVehicleRouteLayer(vehicleLayer);
      setSmartMatchRealLayer(smartMatchRealGraphicsLayer);

      // Función para crear flechas direccionales (CIM: orientación y densidad
      // por zoom las maneja el engine de render, ver utils/routeArrows.js)
      const createDirectionalArrows = (coordinates) => {
        if (coordinates.length < 2) return;
        arrowsLayer.add(buildDirectionArrowsGraphic(coordinates, 'mapmatched'));
      };

      // Función para crear marcadores numerados
      const createNumberedMarkers = (coordinates) => {
        // Crear marcadores numerados para cada punto
        coordinates.forEach((coord, index) => {
          // Crear símbolo de marcador circular
          const markerSymbol = new SimpleMarkerSymbol({
            color: [0, 0, 255, 0.6],
            size: 8,
            outline: {
              color: [255, 255, 255, 0.8],
              width: 1
            }
          });

          // Crear texto con el número
          const textSymbol = new TextSymbol({
            color: "white",
            haloColor: "black",
            haloSize: 1,
            text: `${index + 1}`,
            font: {
              size: 8,
              family: "sans-serif",
              weight: "bold"
            },
            yoffset: -4
          });

          // Crear gráficos
          const pointGraphic = new Graphic({
            geometry: new Point({
              x: coord[0],
              y: coord[1],
              spatialReference: { wkid: 4326 }
            }),
            symbol: markerSymbol
          });

          const textGraphic = new Graphic({
            geometry: new Point({
              x: coord[0],
              y: coord[1],
              spatialReference: { wkid: 4326 }
            }),
            symbol: textSymbol
          });

          markersLayer.addMany([pointGraphic, textGraphic]);
        });
      };

      // Cargar los datos de la ruta si hay un ID
      if (id) {
        const loadRouteData = async () => {
          try {
            const [originalRes, matchedRes] = await Promise.all([
              axios.get(`${process.env.REACT_APP_VALHALLA_URL_BACKEND}/api/mapmatching/ruta/${id}/geojson_original`),
              axios.get(`${process.env.REACT_APP_VALHALLA_URL_BACKEND}/api/mapmatching/ruta/${id}/geojson_mapmatched`)
            ]);

            // Dibujar ruta original
            if (originalRes.data && originalRes.data.type === "Feature" && originalRes.data.geometry) {
              const originalPolyline = {
                type: "polyline",
                paths: [originalRes.data.geometry.coordinates]
              };

              const originalSymbol = {
                type: "simple-line",
                color: [255, 0, 0, 0.8],
                width: 6,
                style: "solid"
              };

              const originalGraphic = new Graphic({
                geometry: originalPolyline,
                symbol: originalSymbol
              });

              originalLayer.add(originalGraphic);

              // Crear marcadores numerados para la ruta original
              createNumberedMarkers(originalRes.data.geometry.coordinates);
            }

            // Dibujar ruta matched y configurar interactividad
            if (matchedRes.data && matchedRes.data.features && matchedRes.data.features.length > 0) {
              matchedRes.data.features.forEach((feature, index) => {
                if (feature.geometry && feature.geometry.coordinates) {
                  const matchedPolyline = {
                    type: "polyline",
                    paths: [feature.geometry.coordinates]
                  };

                  // Crear gráfico con efecto glow
                  const matchedOuterSymbol = {
                    type: "simple-line",
                    color: [0, 255, 0, 0.2],
                    width: 5,
                    style: "solid"
                  };

                  const matchedInnerSymbol = {
                    type: "simple-line",
                    color: [0, 255, 0, 1],
                    width: 2,
                    style: "solid"
                  };

                  const matchedOuterGraphic = new Graphic({
                    geometry: matchedPolyline,
                    symbol: matchedOuterSymbol,
                    attributes: { segmentIndex: index }
                  });

                  const matchedInnerGraphic = new Graphic({
                    geometry: matchedPolyline,
                    symbol: matchedInnerSymbol,
                    attributes: { segmentIndex: index }
                  });

                  matchedLayer.addMany([matchedOuterGraphic, matchedInnerGraphic]);

                  // Crear flechas direccionales para este segmento
                  createDirectionalArrows(feature.geometry.coordinates);
                }
              });

              // Configurar click handler para los segmentos
              mapView.on("click", (event) => {
                const opts = {
                  include: [matchedLayer]
                };

                mapView.hitTest(event, opts).then((response) => {
                  if (response.results.length) {
                    const graphic = response.results[0].graphic;
                    const segmentIndex = graphic.attributes.segmentIndex;
                    setSelectedSegment(segmentIndex);
                  }
                });
              });

              // Actualizar detalles de la ruta
              const routeSegments = matchedRes.data.features.map(feature => ({
                via: {
                  name: feature.properties.name,
                  maxspeed: feature.properties.maxspeed,
                  surface: feature.properties.surface,
                  lane_count: feature.properties.lane_count,
                  bridge: feature.properties.bridge,
                  tunnel: feature.properties.tunnel,
                  roundabout: feature.properties.roundabout
                },
                geometry: feature.geometry,
                timestamp: feature.properties.timestamp || new Date().toISOString()
              }));
              setRouteDetails(routeSegments);
            }

            // Ajustar la vista a los límites de las rutas
            await mapView.when();

            const originalExtent = originalLayer.graphics.length > 0
              ? originalLayer.graphics.getItemAt(0).geometry.extent
              : null;
            const matchedExtent = matchedLayer.graphics.length > 0
              ? matchedLayer.graphics.getItemAt(0).geometry.extent
              : null;

            let fullExtent = null;
            if (originalExtent && matchedExtent) {
              fullExtent = originalExtent.union(matchedExtent);
            } else {
              fullExtent = originalExtent || matchedExtent;
            }

            if (fullExtent) {
              mapView.goTo({
                target: fullExtent.expand(1.2)
              }, {
                duration: 1000,
                easing: "ease-out"
              });
            }

            // Configurar visibilidad de marcadores según el nivel de zoom
            mapView.watch("zoom", (newZoom) => {
              if (markersLayer) {
                markersLayer.visible = newZoom >= 14; // Mostrar marcadores solo cuando el zoom es suficiente
              }
            });

            // Establecer visibilidad inicial
            if (markersLayer) {
              markersLayer.visible = mapView.zoom >= 14;
            }
          } catch (error) {
            console.error('Error loading route data:', error);
          }

          // SmartMatch Real siempre, junto con original + mapmatched (no opt-in
          // manual). Se dibuja directo sobre la capa local (no vía estado
          // React, que en este efecto de montaje todavía no se actualizó).
          try {
            const smartMatchRealRes = await axios.get(`${process.env.REACT_APP_VALHALLA_URL_BACKEND}/api/mapmatching/ruta/${id}/smartmatch-real`);
            if (smartMatchRealRes.data.success) {
              smartMatchRealGraphicsLayer.addMany(buildSmartMatchRealGraphics(smartMatchRealRes.data.data));
              setSmartMatchRealData(smartMatchRealRes.data.data);
              setShowSmartMatchRealRoute(true);
            } else {
              setSmartMatchRealError(smartMatchRealRes.data.error || 'Error desconocido');
            }
          } catch (error) {
            console.error('Error loading SmartMatch Real route:', error);
            setSmartMatchRealError(error.response?.data?.error || error.message);
          }
        };

        loadRouteData();
      }

      return () => {
        // El widget vive en un contenedor propio (no en view.ui), así que
        // destroy() de la vista no lo limpia
        measurement.destroy();
        if (mapView) {
          mapView.destroy();
        }
      };
    }
  }, [id]);

  // Efecto para controlar la visibilidad de las rutas
  useEffect(() => {
    if (vehicleRouteLayer) {
      const graphics = vehicleRouteLayer.graphics.toArray();
      graphics.forEach(graphic => {
        if (graphic.attributes && graphic.attributes.routeType) {
          if (graphic.attributes.routeType === 'original') {
            graphic.visible = showOriginalRoute;
          } else if (graphic.attributes.routeType === 'mapmatched') {
            graphic.visible = showMapmatchedRoute;
          }
        }
      });
    }
  }, [showOriginalRoute, showMapmatchedRoute, vehicleRouteLayer]);

  // Efecto para controlar la visibilidad de la capa SmartMatch Real
  useEffect(() => {
    if (smartMatchRealLayer) {
      smartMatchRealLayer.visible = showSmartMatchRealRoute;
    }
  }, [showSmartMatchRealRoute, smartMatchRealLayer]);

  // Función para hacer zoom a un segmento específico
  const zoomToSegment = (segment) => {
    if (!view || !segment.geometry) return;

    const segmentPolyline = {
      type: "polyline",
      paths: segment.geometry.coordinates
    };

    const graphic = new Graphic({
      geometry: segmentPolyline
    });

    const extent = graphic.geometry.extent;
    if (extent) {
      view.goTo({
        target: extent.expand(1.5)
      }, {
        duration: 1000,
        easing: "ease-out"
      });
    }
  };

  // Función para descargar el GPX original
  const downloadOriginalGPX = () => {
    if (id) {
      window.open(`${process.env.REACT_APP_VALHALLA_URL_BACKEND}/api/mapmatching/ruta/${id}/download_gpx_original`, '_blank');
    } else if (selectedVehicle && dateRange) {
      const params = new URLSearchParams({
        patente: selectedVehicle.patente,
        startDateTime: dateRange.startDateTime,
        endDateTime: dateRange.endDateTime
      });
      window.open(`${process.env.REACT_APP_VALHALLA_URL_BACKEND}/api/vehicles/route/gpx?${params.toString()}`, '_blank');
    }
  };

  // Función para descargar el GPX mapmatched
  const downloadMapmatchedGPX = () => {
    if (id) {
      window.open(`${process.env.REACT_APP_VALHALLA_URL_BACKEND}/api/mapmatching/ruta/${id}/download_gpx_mapmatched`, '_blank');
    } else if (selectedVehicle && dateRange) {
      const params = new URLSearchParams({
        patente: selectedVehicle.patente,
        startDateTime: dateRange.startDateTime,
        endDateTime: dateRange.endDateTime,
        mapmatched: true
      });
      window.open(`${process.env.REACT_APP_VALHALLA_URL_BACKEND}/api/vehicles/route/gpx?${params.toString()}`, '_blank');
    }
  };

  // Manejador para selección de vehículo
  const handleVehicleSelect = (vehicle) => {
    setSelectedVehicle(vehicle);

    // Limpiar la ruta anterior si existe
    if (vehicleRouteLayer) {
      vehicleRouteLayer.removeAll();
    }
  };

  // Manejador para cambio de rango de fechas
  const handleDateRangeChange = async (range) => {
    setDateRange(range);

    // Si tenemos un vehículo seleccionado, obtener su ruta: siempre los dos
    // motores en paralelo (naive Valhalla directo + SmartMatch Real vía
    // proxy), para mostrar las tres líneas (cruda, verde, ámbar) juntas sin
    // un paso manual aparte.
    if (selectedVehicle && range) {
      const naiveFetch = axios.get(`${process.env.REACT_APP_VALHALLA_URL_BACKEND}/api/vehicles/route`, {
        params: {
          patente: selectedVehicle.patente,
          startDateTime: range.startDateTime,
          endDateTime: range.endDateTime
        }
      }).then(response => {
        console.log('response', response);
        if (response.data.success) {
          setVehicleRoute(response.data.data);
          displayVehicleRoute(response.data.data);
        }
      }).catch(error => {
        console.error('Error fetching vehicle route:', error);
      });

      const smartMatchRealFetch = handleFetchSmartMatchReal(selectedVehicle, range);

      await Promise.all([naiveFetch, smartMatchRealFetch]);
    }
  };

  // Función para mostrar la ruta del vehículo en el mapa
  const displayVehicleRoute = (routeData) => {
    console.log('displayVehicleRoute llamado con datos:', routeData);

    if (!view || !vehicleRouteLayer || !routeData || !routeData.coordinates || routeData.coordinates.length === 0) {
      return;
    }

    // Limpiar la capa primero
    vehicleRouteLayer.removeAll();

    // Crear polilínea para la ruta original
    const pathCoordinates = routeData.coordinates.map(coord => [coord.longitude, coord.latitude]);

    const polyline = {
      type: "polyline",
      paths: [pathCoordinates]
    };

    const originalLineSymbol = {
      type: "simple-line",
      color: [255, 0, 0, 0.8], // Rojo para la ruta original
      width: 4,
      style: "solid"
    };

    const originalRouteGraphic = new Graphic({
      geometry: polyline,
      symbol: originalLineSymbol,
      attributes: {
        routeType: 'original'
      }
    });

    vehicleRouteLayer.add(originalRouteGraphic);

    // Si hay ruta mapmatched, mostrarla en verde
    let mapmatchedCoordinates = null;
    if (routeData.mapmatched_coordinates && routeData.mapmatched_coordinates.length > 0) {
      mapmatchedCoordinates = routeData.mapmatched_coordinates.map(coord => [coord.longitude, coord.latitude]);

      const mapmatchedPolyline = {
        type: "polyline",
        paths: [mapmatchedCoordinates]
      };

      const mapmatchedLineSymbol = {
        type: "simple-line",
        color: [0, 255, 0, 0.8], // Verde para la ruta mapmatched
        width: 4,
        style: "solid"
      };

      const mapmatchedRouteGraphic = new Graphic({
        geometry: mapmatchedPolyline,
        symbol: mapmatchedLineSymbol,
        attributes: {
          routeType: 'mapmatched'
        }
      });

      vehicleRouteLayer.add(mapmatchedRouteGraphic);
    }

    // Añadir marcadores para los puntos originales con información detallada
    routeData.coordinates.forEach((coord, index) => {
      if (index <= 5) {
        console.log(index, ' - coord  => ', coord);
      }
      const point = new Point({
        x: coord.longitude,
        y: coord.latitude,
        spatialReference: { wkid: 4326 }
      });

      const markerSymbol = new SimpleMarkerSymbol({
        color: [255, 0, 0, 0.6],
        size: 6,  // Reducido el tamaño para que no se vea tan saturado
        outline: {
          color: [255, 255, 255, 0.8],
          width: 1
        }
      });

      const pointGraphic = new Graphic({
        geometry: point,
        symbol: markerSymbol,
        attributes: {
          index: index,
          latitude: coord.latitude,
          longitude: coord.longitude,
          timestamp: coord.timestamp,
          speed: coord.speed || 0,
          heading: coord.heading || 0,
          type: 'routePoint'
        }
      });

      vehicleRouteLayer.add(pointGraphic);
    });

    // Flechas de dirección (celestes) sobre ambas rutas; se añaden al final
    // para que pinten encima de líneas y puntos. Llevan routeType para que
    // los switches de visibilidad las oculten junto a su línea.
    if (pathCoordinates.length >= 2) {
      vehicleRouteLayer.add(buildDirectionArrowsGraphic(pathCoordinates, 'original'));
    }
    if (mapmatchedCoordinates && mapmatchedCoordinates.length >= 2) {
      vehicleRouteLayer.add(buildDirectionArrowsGraphic(mapmatchedCoordinates, 'mapmatched'));
    }

    // Añadir click handler para los puntos
    if (!view.clickHandlerAdded) {
      view.on("click", (event) => {
        const opts = {
          include: [vehicleRouteLayer]
        };

        view.hitTest(event, opts).then((response) => {
          // Las flechas de dirección quedan encima de los puntos: buscar el
          // routePoint en todos los resultados, no solo en el primero
          const pointResult = response.results.find(
            (r) => r.graphic?.attributes?.type === 'routePoint'
          );
          if (pointResult) {
            setSelectedPoint(pointResult.graphic.attributes);
            setPointPopupPosition({
              x: event.screenPoint.x,
              y: event.screenPoint.y
            });
            setPointPopupVisible(true);
          } else {
            setPointPopupVisible(false);
          }
        });
      });
      view.clickHandlerAdded = true;
    }

    // Ajustar la vista a los límites de la ruta
    const routeExtent = originalRouteGraphic.geometry.extent;
    if (routeExtent) {
      view.goTo({
        target: routeExtent.expand(1.2)
      }, {
        duration: 1000,
        easing: "ease-out"
      });
    }

    // Establecer los datos y habilitar playback
    setVehicleRoute(routeData);

    // Activar el playback de forma forzada después de un pequeño delay
    // para asegurar que los estados se hayan actualizado
    setTimeout(() => {
      console.log('Activando playback después de cargar ruta');
      setPlaybackEnabled(true);
    }, 500);
  };

  // Manejador para pedir y mostrar la ruta vía el pipeline REAL de producción
  // vehicleOverride/rangeOverride: para llamar esto justo después de
  // setSelectedVehicle/setDateRange, cuando el estado de React todavía no
  // refleja el valor recién elegido (p. ej. desde handleDateRangeChange).
  const handleFetchSmartMatchReal = async (vehicleOverride, rangeOverride) => {
    const vehicle = vehicleOverride || selectedVehicle;
    const range = rangeOverride || dateRange;

    setSmartMatchRealError(null);
    setSmartMatchRealLoading(true);
    try {
      let response;
      if (id) {
        response = await axios.get(`${process.env.REACT_APP_VALHALLA_URL_BACKEND}/api/mapmatching/ruta/${id}/smartmatch-real`);
      } else if (vehicle && range) {
        response = await axios.get(`${process.env.REACT_APP_VALHALLA_URL_BACKEND}/api/vehicles/route/smartmatch-real`, {
          params: {
            patente: vehicle.patente,
            startDateTime: range.startDateTime,
            endDateTime: range.endDateTime
          }
        });
      } else {
        setSmartMatchRealError('Selecciona un vehículo y un rango de fechas, o carga un GPX');
        return;
      }

      if (response.data.success) {
        setSmartMatchRealData(response.data.data);
        setShowSmartMatchRealRoute(true);
        displaySmartMatchRealRoute(response.data.data);
      } else {
        setSmartMatchRealError(response.data.error || 'Error desconocido');
      }
    } catch (error) {
      console.error('Error fetching SmartMatch Real route:', error);
      setSmartMatchRealError(error.response?.data?.error || error.message);
    } finally {
      setSmartMatchRealLoading(false);
    }
  };

  // Función para mostrar la capa "SmartMatch Real" en el mapa: usa
  // displaySmartMatchRealRoute (vía estado smartMatchRealLayer) para el flujo
  // de búsqueda por vehículo/fecha; el flujo por GPX subido (loadRouteData, en
  // el useEffect de montaje) llama directo a smartMatchRealLayer.addMany(...)
  // con la capa local para evitar depender del estado, que en ese punto del
  // ciclo de vida todavía no se actualizó (closure obsoleto del efecto de
  // montaje).
  const displaySmartMatchRealRoute = (data) => {
    if (!view || !smartMatchRealLayer || !data) return;
    smartMatchRealLayer.removeAll();
    smartMatchRealLayer.addMany(buildSmartMatchRealGraphics(data));
  };

  // Manejador para click en pórtico
  const handlePorticoClick = (portico) => {
    setSelectedPortico({
      ...portico,
      concesionaria: {
        concesionaria: portico.concesionaria,
        tipo: portico.tipo_concesionaria,
        cod_via: portico.cod_via,
        observaciones: portico.observaciones
      }
    });
    setPorticoPopupVisible(true);

    // Usar la posición del pórtico para posicionar el popup
    if (view) {
      const point = new Point({
        x: portico.longitud,
        y: portico.latitud,
        spatialReference: { wkid: 4326 }
      });

      const screenPoint = view.toScreen(point);
      setPorticoPopupPosition({
        x: screenPoint.x,
        y: screenPoint.y
      });
    }
  };

  // Manejador para seleccionar un pórtico desde la búsqueda (PorticoSearch):
  // vuela el mapa hasta su posición y recién ahí abre el popup — si se abriera
  // antes de que goTo() termine, view.toScreen() calcularía la posición vieja
  // (la de antes de mover el mapa) y el popup quedaría mal ubicado.
  const handlePorticoSearchSelect = async (portico) => {
    if (portico.longitud == null || portico.latitud == null) return;
    setShowPorticoLayer(true);
    if (view) {
      const point = new Point({
        x: portico.longitud,
        y: portico.latitud,
        spatialReference: { wkid: 4326 }
      });
      await view.goTo({ target: point, zoom: 17 }, { duration: 800, easing: 'ease-out' });
    }
    handlePorticoClick(portico);
  };

  // Manejador para cambio de tabs
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Manejador para toggle de la capa de pórticos
  const togglePorticoLayer = () => {
    setShowPorticoLayer(!showPorticoLayer);
  };

  useEffect(() => {
    if (view) {
      // Obtener el elemento del panel de filtros
      const filterPanel = document.getElementById('filterPanel');
      if (filterPanel) {
        // Obtener el contenedor creado en el mapa
        const filterContainer = view.ui.find('filter-container');
        if (filterContainer) {
          // Limpiar el contenedor y agregar el panel
          filterContainer.innerHTML = '';
          filterContainer.appendChild(filterPanel);
          filterPanel.style.display = 'block';
        }
      }
    }
  }, [view, showOriginalRoute, showMapmatchedRoute, showPorticoLayer]);

  // Function to toggle measurement widget (el contenedor se muestra/oculta
  // vía isMeasuring en el JSX)
  const toggleMeasurement = () => {
    if (measurementWidget) {
      if (!isMeasuring) {
        measurementWidget.viewModel.start();
      } else {
        measurementWidget.viewModel.clear();
      }
      setIsMeasuring(!isMeasuring);
    }
  };

  // Mejorar la función de activación del playback
  // const enableRoutePlayback = () => {
  //   console.log('Intentando habilitar playback:', 
  //     { vehicleRouteExists: !!vehicleRoute, coordsLength: vehicleRoute?.coordinates?.length });

  //   if (vehicleRoute && vehicleRoute.coordinates && vehicleRoute.coordinates.length > 0) {
  //     console.log('Habilitando playback');
  //     setPlaybackEnabled(true);
  //   } else {
  //     console.log('No se puede habilitar playback: datos insuficientes');
  //   }
  // };

  // Función para manejar cambios en la posición del playback
  const handlePlaybackPositionChange = (position) => {
    setCurrentPlaybackPosition(position);
  };

  // Añadir un efecto para monitorear los cambios en vehicleRoute
  useEffect(() => {
    if (vehicleRoute) {
      console.log('vehicleRoute actualizado:',
        { hasData: !!vehicleRoute, coordsLength: vehicleRoute?.coordinates?.length });
    }
  }, [vehicleRoute]);

  // Añadir un efecto para monitorear playbackEnabled
  useEffect(() => {
    console.log('Estado de playbackEnabled cambiado a:', playbackEnabled);
  }, [playbackEnabled]);

  return (
    <Box sx={{
      position: 'fixed',
      top: 64, // Altura del AppBar
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'hidden'
    }}>
      {/* Mapa (ahora ocupa el espacio debajo del AppBar) */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        '& .esri-view': {
          position: 'absolute',
          height: '100%',
          width: '100%',
          zIndex: 0
        }
      }}>
        <div ref={mapDiv} style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%'
        }} />
      </Box>

      {/* Menu Button - ajustado para estar debajo del AppBar */}
      <IconButton
        sx={{
          position: 'absolute',
          top: 80,
          left: 13,
          backgroundColor: 'white',
          '&:hover': {
            backgroundColor: '#f5f5f5'
          },
          zIndex: 1200
        }}
        onClick={() => setIsMenuOpen(!isMenuOpen)}
      >
        {isMenuOpen ? <CloseIcon /> : <MenuIcon />}
      </IconButton>

      {/* Botón flotante de medición - siempre visible sobre el mapa */}
      <Tooltip title={isMeasuring ? "Detener medición" : "Medir distancia"} placement="right">
        <IconButton
          sx={{
            position: 'absolute',
            top: 128,
            left: 13,
            backgroundColor: isMeasuring ? 'primary.main' : 'white',
            color: isMeasuring ? 'white' : 'inherit',
            '&:hover': {
              backgroundColor: isMeasuring ? 'primary.dark' : '#f5f5f5'
            },
            zIndex: 1200
          }}
          onClick={toggleMeasurement}
        >
          <SquareFootIcon />
        </IconButton>
      </Tooltip>

      {/* Panel del widget de medición: sobre la barra de playback para que no quede tapado */}
      <Box
        ref={measurementContainerRef}
        sx={{
          position: 'absolute',
          bottom: 140,
          left: 13,
          zIndex: 1200,
          display: isMeasuring ? 'block' : 'none',
          backgroundColor: 'white',
          borderRadius: 1,
          boxShadow: 3,
          p: 1.5,
          maxWidth: 320
        }}
      />

      {/* Panel lateral flotante - ajustado para estar debajo del AppBar */}
      {isMenuOpen && (
        <Box
          sx={{
            position: 'absolute',
            top: 80,
            left: 48,
            width: 300,
            zIndex: 1100,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            maxHeight: 'calc(100vh - 96px)', // Ajustado para considerar el AppBar
          }}
        >
        <Paper
          elevation={3}
          sx={{
            width: 300,
            overflow: 'auto',
            backgroundColor: 'background.paper',
            borderRadius: 2,
            '& .MuiList-root': {
              padding: 0
            },
            minHeight: '390px',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: 'white' }}
          >
            <Tab label="Ruta" />
            <Tab label="Vehículos" />
          </Tabs>

          {selectedTab === 0 && (
            <>
              {/* Botón de subir GPX */}
              <Box sx={{ p: 2, borderBottom: '1px solid #ccc', backgroundColor: 'white' }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  startIcon={<UploadFileIcon />}
                  component="label"
                >
                  Subir GPX
                  <input
                    type="file"
                    hidden
                    accept=".gpx"
                    onChange={async (event) => {
                      const file = event.target.files[0];
                      if (file) {
                        const formData = new FormData();
                        formData.append('gpx', file);
                        try {
                          const response = await axios.post(`${process.env.REACT_APP_VALHALLA_URL_BACKEND}/api/mapmatching/uploadgpx`, formData);
                          if (response.data.id) {
                            window.location.href = `/view/${response.data.id}`;
                          }
                        } catch (error) {
                          console.error('Error uploading file:', error);
                          alert('Error al subir el archivo');
                        }
                      }
                    }}
                  />
                </Button>
              </Box>

              {/* Botones de descarga */}
              <Box sx={{ p: 2, borderBottom: '1px solid #ccc', backgroundColor: 'white' }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={downloadOriginalGPX}
                  disabled={!id && (!selectedVehicle || !dateRange)}
                  sx={{ mb: 1 }}
                >
                  Descargar GPX Original
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={downloadMapmatchedGPX}
                  disabled={!id && (!selectedVehicle || !dateRange)}
                >
                  Descargar GPX Mapmatched
                </Button>
              </Box>

              {/* Botón SmartMatch Real: pega al pipeline real de producción */}
              <Box sx={{ p: 2, borderBottom: '1px solid #ccc', backgroundColor: 'white' }}>
                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  onClick={handleFetchSmartMatchReal}
                  disabled={smartMatchRealLoading || (!id && (!selectedVehicle || !dateRange))}
                >
                  {smartMatchRealLoading ? 'Calculando...' : 'Ver SmartMatch Real'}
                </Button>
                {smartMatchRealError && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                    {smartMatchRealError}
                  </Typography>
                )}
              </Box>

              {/* Lista de segmentos */}
              <List sx={{
                width: '100%',
                backgroundColor: 'white'
              }}>
                {routeDetails?.map((segment, index) => (
                  <ListItem
                    key={index}
                    disablePadding
                    selected={selectedSegment === index}
                  >
                    <ListItemButton onClick={() => zoomToSegment(segment)}>
                      <Box>
                        <Box sx={{ fontWeight: 'bold', mb: 1 }}>Street {index + 1}</Box>
                        <Box sx={{ color: 'text.secondary' }}>
                          <Box sx={{ mb: 0.5 }}>Name: {segment.via.name || 'Unknown'}</Box>
                          <Box sx={{ mb: 0.5 }}>Time: {moment(segment.timestamp).format('YYYY-MM-DD HH:mm:ss')}</Box>
                          <Box sx={{ mb: 0.5 }}>Speed: {segment.via.maxspeed} km/h</Box>
                          <Box sx={{ mb: 0.5 }}>Surface: {segment.via.surface}</Box>
                          <Box sx={{ mb: 0.5 }}>Lanes: {segment.via.lane_count}</Box>
                          {segment.via.bridge && <Box sx={{ mb: 0.5 }}>Bridge: Yes</Box>}
                          {segment.via.tunnel && <Box sx={{ mb: 0.5 }}>Tunnel: Yes</Box>}
                          {segment.via.roundabout && <Box sx={{ mb: 0.5 }}>Roundabout: Yes</Box>}
                        </Box>
                      </Box>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </>
          )}

          {selectedTab === 1 && (
            <Box sx={{ p: 2, backgroundColor: 'white', flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Búsqueda de vehículos
              </Typography>

              <SearchVehicle onVehicleSelect={handleVehicleSelect} />

              {selectedVehicle && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Vehículo seleccionado
                  </Typography>
                  <Typography>
                    Patente: {selectedVehicle.patente}
                  </Typography>
                  <Typography>
                    Usuario: {selectedVehicle.user}
                  </Typography>
                  <Typography>
                    {selectedVehicle.marca} {selectedVehicle.modelo} ({selectedVehicle.anio})
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <DateTimeSelector onDateRangeChange={handleDateRangeChange} />
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Button
                variant="contained"
                color={showPorticoLayer ? "secondary" : "primary"}
                fullWidth
                onClick={togglePorticoLayer}
                sx={{ mt: 'auto' }}
              >
                {showPorticoLayer ? "Ocultar pórticos" : "Mostrar pórticos"}
              </Button>
            </Box>
          )}
        </Paper>

        {/* Buscador de pórticos: fuera del panel de tabs para que no dependa
            de su alto dinámico ni de qué tab esté seleccionado */}
        <Paper
          elevation={3}
          sx={{
            width: 300,
            p: 2,
            backgroundColor: 'background.paper',
            borderRadius: 2
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Ubicar pórtico
          </Typography>
          <PorticoSearch onPorticoSelect={handlePorticoSearchSelect} />
        </Paper>
        </Box>
      )}

      {/* Panel de Filtros flotante */}
      <Paper
        elevation={3}
        sx={{
          position: 'absolute',
          top: 16,
          right: 14,  // Ajustado para no solaparse con otros elementos
          padding: 2,
          backgroundColor: 'white',
          zIndex: 1100,  // Aumentado para estar sobre el mapa
          borderRadius: 2,
          minWidth: 200
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
          Filtros de Visualización
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showOriginalRoute}
                onChange={(e) => setShowOriginalRoute(e.target.checked)}
                color="error"
              />
            }
            label="Ruta Original"
          />
          <FormControlLabel
            control={
              <Switch
                checked={showMapmatchedRoute}
                onChange={(e) => setShowMapmatchedRoute(e.target.checked)}
                color="success"
              />
            }
            label="Ruta Mapmatched"
          />
          <FormControlLabel
            control={
              <Switch
                checked={showPorticoLayer}
                onChange={() => setShowPorticoLayer(!showPorticoLayer)}
                color="primary"
              />
            }
            label="Mostrar Pórticos"
          />
          <FormControlLabel
            control={
              <Switch
                checked={showSmartMatchRealRoute}
                onChange={(e) => setShowSmartMatchRealRoute(e.target.checked)}
                color="warning"
                disabled={!smartMatchRealData}
              />
            }
            label="SmartMatch Real"
          />
        </Box>
      </Paper>

      {/* Pórticos y Popup */}
      {showPorticoLayer && view && (
        <PorticoLayer
          mapView={view}
          onPorticoClick={handlePorticoClick}
        />
      )}


      {porticoPopupVisible && selectedPortico && (
        <PorticoPopup
          portico={selectedPortico}
          concesionaria={selectedPortico.concesionaria}
          position={porticoPopupPosition}
          onClose={() => setPorticoPopupVisible(false)}
          view={view}
        />
      )}


      {/* Point Info Popup */}
      {/* Point Info Popup - Smart Positioning */}
      {pointPopupVisible && selectedPoint && (
        <PointPopup
          point={selectedPoint}
          position={pointPopupPosition}
          onClose={() => setPointPopupVisible(false)}
          view={view}
        />
      )}

      {/* Componente de Playback - mejorado con mejor condición */}
      {playbackEnabled && view && (
        <RoutePlayback
          mapView={view}
          routeData={vehicleRoute || { coordinates: [] }}
          onPositionChange={handlePlaybackPositionChange}
        />
      )}
    </Box>
  );
};

export default MapViewComponent; 